import angular from 'angular'

import { makeOR, makeEQ, makeAND, zeroIfNaN } from '../../utils/bits'
import { get, wrap, once, noop, constant, stubTrue, uniqueId } from 'lodash-es'

import envRaw from '../../utils/env-raw'
import setCss from '../../utils/set-css'
import Emitter from '../../utils/emitter'
import StrictObj from '../../utils/strict-obj'
import smoothScroll from '../../utils/smooth-scroll'
import attachSetters from '../../utils/attach-setters'
import attachGetters from '../../utils/attach-getters'
import attachEmitters from '../../utils/attach-emitters'
import ScrollDetector from '../../utils/scroll-detector'

import { defineGetters } from '../../utils/define-getter'
import { fmtTranslateX } from '../../utils/fmt-translate'

import Template from './gallery-slider.component.ejs'
import LEFT_ICON from '../../../img/slider-buttons/bright-left.svg'
import RIGHT_ICON from '../../../img/slider-buttons/bright-right.svg'

angular
  .module('ggs.gallery-slider')
  .component('ggsGallerySlider', {
    template: Template(StrictObj({
      LE: envRaw.liveEditMode,
      LEFT_ICON,
      RIGHT_ICON
    })),
    controller: GallerySliderComponent,
    bindings: {
      ggsPost: '<',
      ggsProperties: '<',
      ggsIsTinyLayout: '<',
      ggsEmbedLoadHandler: '<',
      ggsEmbedUnloadHandler: '<',
      ggsEmbedAttachCallback: '<',
      ggsContentReadyHandler: '<',
      ggsContentChangeHandler: '<',
      ggsContentAttachCallback: '<',
      ggsSliderChangeHandler: '<',
      ggsSliderAttachCallback: '<'
    }
  })

const BLINK_DELAY = 2000

const QS_CONTENT = '.ggs-gallery-slider__content'
const QS_SHOWCASE = '.ggs-gallery-slider__showcase'

const TRANSFORM = 'transform'
const TRANSITION = 'transition'

const TRANSITION_NONE = 'transform 0ms'
const TRANSITION_ANIM = 'transform 300ms'

const INSTAGRAM_STORY = 'instagram_story'

function GallerySliderComponent(
  $q,
  qh,
  $log,
  domh,
  $scope,
  debugObj,
  $element,
  LazyResource,
  DefineMethod,
  DefineHandler,
  isTextContent,
  isImageContent,
  isEmbedContent) {

  const $ctrl = $scope.$ctrl = attachGetters(attachSetters(StrictObj(this)))
  const sign = `gallerySlider[id=${$ctrl.ggsPost.id}][uid=${uniqueId()}]`
  debugObj(sign, $ctrl)

  $ctrl.wrapM = DefineMethod(sign)
  $ctrl.wrapH = DefineHandler(sign)

  $ctrl.slideList = SlideList()

  $ctrl.syncIndex = 0
  $ctrl.viewIndex = 0
  $ctrl.viewIndexEmitter = Emitter(`${sign}:viewIndex`)

  $ctrl.showsBadge = false
  $ctrl.showsBadgeUid = 0
  $ctrl.showsBadgeEmitter = Emitter(`${sign}:showsBadge`)

  $ctrl.isAllowed = false
  $ctrl.isDebouncing = false

  $ctrl.showcaseSizeEmitter = Emitter(`${sign}:showcaseSize`)
  $ctrl.showcaseSize = {
    width: undefined,
    height: undefined
  }

  const contentElm = domh.find($element, QS_CONTENT)
  const showcaseElm = domh.find($element, QS_SHOWCASE)
  const showcaseSD = ScrollDetector(showcaseElm)

  if (envRaw.useTransformMode) {
    setCss(showcaseElm, { overflowX: 'hidden' })
  }

  $ctrl.isTinyLayout = attachEmitters(
    $ctrl.ggsIsTinyLayout,
    $ctrl.ggsProperties.key2emitter.slider_force_tiny,
    makeOR(
      $ctrl.ggsIsTinyLayout,
      $ctrl.ggsProperties.key2get.slider_force_tiny))

  // slider -> content

  $ctrl.wrapM('callEmbedActionMethod',
    qh.makeJoin(
      $ctrl.slideList.syncSide.extEmbed.disallow,
      qh.makeIf($ctrl.getters.isAllowed, $ctrl.slideList.syncCurr.extEmbed.allow),
      qh.makeIf($ctrl.getters.isDebouncing, $ctrl.slideList.syncCurr.extEmbed.debounce),
      qh.makeIf(
        makeAND(
          $ctrl.getters.negate.isAllowed,
          $ctrl.getters.negate.isDebouncing),
        $ctrl.slideList.syncCurr.extEmbed.disallow)))

  // slider -> modal

  $ctrl.wrapM('callEmbedStateHandler',
    qh.makeIfElse(
      $ctrl.getters.slideList.syncCurr.loaded,
      $ctrl.ggsEmbedLoadHandler,
      $ctrl.ggsEmbedUnloadHandler))

  $ctrl.wrapM('callContentReadyHandler',
    qh.makeIf(
      $ctrl.getters.slideList.all.ready,
      once($ctrl.ggsContentReadyHandler)))

  $ctrl.wrapM('callContentChangeHandler',
    $ctrl.ggsContentChangeHandler)

  // modal -> slider

  $ctrl.extContentCalcWidth = (showcaseHeight) => {
    return $ctrl.slideList.first.extContent.calcWidth(showcaseHeight)
  }

  $ctrl.extContentCalcHeight = (showcaseWidth) => {
    return $ctrl.slideList.first.extContent.calcHeight(showcaseWidth)
  }

  $ctrl.wrapM('extContentApplySize', (showcaseSize) => {
    $ctrl.showcaseSize = {
      width: showcaseSize.width,
      height: showcaseSize.height
    }
    $ctrl.showcaseSizeEmitter.emit()

    return qh.join(
      $ctrl.jumpToSync(),
      $ctrl.slideList.all.extContent.applySize(showcaseSize))
  })

  $ctrl.wrapM('extEmbedAllow', () => {
    $ctrl.isAllowed = true
    $ctrl.isDebouncing = false
    return $ctrl.callEmbedActionMethod()
  })

  $ctrl.wrapM('extEmbedDebounce', () => {
    $ctrl.isAllowed = false
    $ctrl.isDebouncing = true
    return qh.join(
      $ctrl.blinkBadge(),
      $ctrl.callEmbedActionMethod())
  })

  $ctrl.wrapM('extEmbedDisallow', () => {
    $ctrl.isAllowed = false
    $ctrl.isDebouncing = false
    return $ctrl.callEmbedActionMethod()
  })

  // own features

  $ctrl.wrapM('blinkBadge', () => {
    $ctrl.showsBadge = true
    $ctrl.showsBadgeUid++
    $ctrl.showsBadgeEmitter.emit()
    return qh.delay(BLINK_DELAY).then(
      qh.makeIf(
        makeEQ(
          $ctrl.getters.showsBadgeUid,
          constant($ctrl.showsBadgeUid)),
        qh.makeOver(
          $ctrl.setters.bindFalse.showsBadge,
          $ctrl.showsBadgeEmitter.emit)))
  })

  $ctrl.wrapM('jumpTo', (index) => {
    const goalOffset = index * $ctrl.showcaseSize.width
    if (envRaw.useTransformMode) {
      setCss(contentElm, TRANSITION, TRANSITION_NONE)
      setCss(contentElm, TRANSFORM, fmtTranslateX(-1 * goalOffset))
      return qh.join($ctrl.applyViewIndex(index), $ctrl.applySyncIndex(index))
    } else {
      showcaseElm.scrollLeft = goalOffset
    }
  })

  $ctrl.wrapM('scrollTo', (index) => {
    const scrollLeft = showcaseSD.getLeft()
    const goalOffset = index * $ctrl.showcaseSize.width
    const duration = Math.round(zeroIfNaN(Math.abs(scrollLeft - goalOffset) / envRaw.sliderScrollSpeed))
    if (envRaw.useTransformMode) {
      setCss(contentElm, TRANSITION, TRANSITION_ANIM)
      setCss(contentElm, TRANSFORM, fmtTranslateX(-1 * goalOffset))
      return qh.join($ctrl.applyViewIndex(index), $ctrl.applySyncIndex(index))
    } else {
      smoothScroll.left(showcaseElm, goalOffset, duration, envRaw.sliderScrollEaseFunc)
    }
  })

  $ctrl.wrapM('jumpToSync',
    qh.makeFlow(
      $ctrl.getters.slideList.syncCurr.index,
      $ctrl.jumpTo))

  $ctrl.wrapM('scrollToSync',
    qh.makeFlow(
      $ctrl.getters.slideList.syncCurr.index,
      $ctrl.scrollTo))

  $ctrl.wrapM('prev',
    qh.makeFlow(
      $ctrl.getters.slideList.viewPrev.index,
      $ctrl.scrollTo))

  $ctrl.wrapM('next',
    qh.makeFlow(
      $ctrl.getters.slideList.viewNext.index,
      $ctrl.scrollTo))

  $ctrl.wrapH('prevRequestHandler',
    qh.makeIf(
      $ctrl.getters.slideList.all.ready,
      $ctrl.prev))

  $ctrl.wrapH('nextRequestHandler',
    qh.makeIf(
      $ctrl.getters.slideList.all.ready,
      $ctrl.next))

  $ctrl.wrapH('slideRequestHandler', $ctrl.scrollTo)

  $ctrl.wrapM('applySyncIndex', (syncIndex) => {
    if ($ctrl.syncIndex === syncIndex) {
      return
    }

    $ctrl.syncIndex = syncIndex
    return qh.join(
      $ctrl.callEmbedActionMethod(),
      $ctrl.callEmbedStateHandler(),
      $ctrl.ggsSliderChangeHandler($ctrl.syncIndex))
  })

  $ctrl.wrapM('applyViewIndex', (viewIndex) => {
    if ($ctrl.viewIndex === viewIndex) {
      return
    }

    $ctrl.viewIndex = viewIndex
    $ctrl.viewIndexEmitter.emit()

    $ctrl.slideList.viewCurr.exclude($ctrl.syncIndex).extEmbed.debounce()
    $ctrl.slideList.viewSide.exclude($ctrl.syncIndex).extEmbed.disallow()

    return $ctrl.blinkBadge()
  })

  $ctrl.wrapH('scrollHandler', qh.makeIf($ctrl.getters.slideList.all.ready, (scrollLeft, _scrollTop, isScrollFinished) => {
    $log.debug(`${sign}#scrollHandler: scrollLeft=${scrollLeft}, isScrollFinished=${isScrollFinished}`)
    if (!isScrollFinished) {
      const floatIndex = scrollLeft / $ctrl.showcaseSize.width
      const viewIndex = Math.round(floatIndex)
      return $ctrl.applyViewIndex(viewIndex)
    } else {
      const syncOffset = $ctrl.syncIndex * $ctrl.showcaseSize.width
      if (Math.abs(syncOffset - scrollLeft) <= $ctrl.showcaseSize.width / 10) {
        return qh.join(
          $ctrl.scrollTo($ctrl.syncIndex),
          $ctrl.applySyncIndex($ctrl.syncIndex))
      }

      const floatIndex = scrollLeft / $ctrl.showcaseSize.width
      const naiveIndex = floatIndex > $ctrl.syncIndex
        ? Math.ceil(floatIndex)
        : Math.floor(floatIndex)

      const syncIndex = Math.abs($ctrl.syncIndex - naiveIndex) > 1
        ? Math.round(floatIndex)
        : naiveIndex

      return qh.join(
        $ctrl.scrollTo(syncIndex),
        $ctrl.applySyncIndex(syncIndex))
    }
  }))

  // extSlider

  $ctrl.wrapM('extSliderHookPrev',
    qh.makeIf(
      makeAND(
        $ctrl.getters.slideList.all.ready,
        $ctrl.getters.slideList.viewPrev.exist),
      qh.makeFlow($ctrl.prev, stubTrue)))

  $ctrl.wrapM('extSliderHookNext',
    qh.makeIf(
      makeAND(
        $ctrl.getters.slideList.all.ready,
        $ctrl.getters.slideList.viewNext.exist),
      qh.makeFlow($ctrl.next, stubTrue)))

  $ctrl.wrapM('extSliderNthSlide',
    qh.makeIf(
      $ctrl.getters.slideList.all.ready,
      $ctrl.jumpTo))

  $ctrl.wrapM('extSliderLastSlide',
    qh.makeIf(
      $ctrl.getters.slideList.all.ready,
      qh.makeFlow(
        $ctrl.getters.slideList.last.index,
        $ctrl.jumpTo)))

  $ctrl.wrapM('extSliderFirstSlide',
    qh.makeIf(
      $ctrl.getters.slideList.all.ready,
      qh.makeFlow(
        $ctrl.getters.slideList.first.index,
        $ctrl.jumpTo)))

  // $onInit

  $ctrl.wrapM('attachToParent', () => {
    return qh.join(
      $ctrl.ggsEmbedAttachCallback(
        $ctrl.extEmbedAllow,
        $ctrl.extEmbedDebounce,
        $ctrl.extEmbedDisallow),
      $ctrl.ggsContentAttachCallback(
        $ctrl.extContentCalcWidth,
        $ctrl.extContentCalcHeight,
        $ctrl.extContentApplySize),
      $ctrl.ggsSliderAttachCallback(
        $ctrl.extSliderHookPrev,
        $ctrl.extSliderHookNext,
        $ctrl.extSliderNthSlide,
        $ctrl.extSliderLastSlide,
        $ctrl.extSliderFirstSlide))
  })

  $ctrl.wrapM('attachToEmitters', () => {
    showcaseSD.left.on($ctrl.scrollHandler)
  })

  $ctrl.wrapH('$onInit',
    qh.makeJoin(
      $ctrl.attachToParent,
      $ctrl.attachToEmitters,
      $ctrl.callContentReadyHandler))

  // $onDestroy

  $ctrl.wrapM('detachFromEmitters', () => {
    showcaseSD.left.off($ctrl.scrollHandler)
  })

  $ctrl.wrapH('$onDestroy',
    qh.makeJoin(
      $ctrl.detachFromEmitters,
      wrap($ctrl, debugObj.remove)))

  // slide

  function SlideList() {
    const slideList = StrictObj($ctrl.ggsPost.media_list.map(Slide))
    const lastIndex = slideList.length - 1
    const firstIndex = 0

    slideList.all = makeSumProxy(stubTrue)
    slideList.last = makeSoleProxy((slide) => slide.index === lastIndex)
    slideList.first = makeSoleProxy((slide) => slide.index === firstIndex)

    slideList.viewSide = makeSumProxy((slide) => slide.index !== $ctrl.viewIndex)
    slideList.viewCurr = makeSoleProxy((slide) => slide.index === $ctrl.viewIndex)
    slideList.viewPrev = makeSoleProxy((slide) => slide.index === $ctrl.viewIndex - 1, firstIndex)
    slideList.viewNext = makeSoleProxy((slide) => slide.index === $ctrl.viewIndex + 1, lastIndex)

    slideList.syncSide = makeSumProxy((slide) => slide.index !== $ctrl.syncIndex)
    slideList.syncCurr = makeSoleProxy((slide) => slide.index === $ctrl.syncIndex)
    slideList.syncPrev = makeSoleProxy((slide) => slide.index === $ctrl.syncIndex - 1, firstIndex)
    slideList.syncNext = makeSoleProxy((slide) => slide.index === $ctrl.syncIndex + 1, lastIndex)

    return slideList

    function makeSoleProxy(query, defIndex = null) {
      return makeProxy(query, true, defIndex)
    }

    function makeSumProxy(query, defIndex = null) {
      return makeProxy(query, false, defIndex)
    }

    function makeProxy(query, sole, defIndex = null) {
      const proxy = StrictObj({
        exclude,
        extEmbed: StrictObj({
          allow: makeAsync('extEmbed.allow', query, sole),
          debounce: makeAsync('extEmbed.debounce', query, sole),
          disallow: makeAsync('extEmbed.disallow', query, sole)
        }),
        extContent: StrictObj({
          calcWidth: makeSync('extContent.calcWidth', query, sole),
          calcHeight: makeSync('extContent.calcHeight', query, sole),
          applySize: makeAsync('extContent.applySize', query, sole)
        })
      })

      defineGetters(proxy, { index: makeGet('index', query, sole, defIndex) })
      defineGetters(proxy, { exist: makeCheck('exist', query, sole) })
      defineGetters(proxy, { ready: makeCheck('ready', query, sole) })
      defineGetters(proxy, { loaded: makeCheck('loaded', query, sole) })
      defineGetters(proxy, { isStory: makeCheck('isStory', query, sole) })

      return proxy

      function exclude(excludeIndex) {
        return makeProxy((slide) => query(slide) && slide.index !== excludeIndex, sole, defIndex)
      }
    }

    function makeGet(path, query, sole, def) {
      const val = (slide) => get(slide, path, def)
      const map = (array) => array.map(val)
      const find = (array) => array.find(query)
      const filter = (array) => array.filter(query)
      return () => sole ? val(find(slideList)) : map(filter(slideList))
    }

    function makeCheck(path, query, sole) {
      const one = (slide) => !!get(slide, path)
      const all = (array) => array.every(one)
      const find = (array) => array.find(query)
      const filter = (array) => array.filter(query)
      return () => sole ? one(find(slideList)) : all(filter(slideList))
    }

    function makeSync(path, query, sole) {
      const end = (result) => sole ? result[0] : result
      const map = (array, args) => array.map((slide) => get(slide, path)(...args))
      const filter = (array) => array.filter(query)
      return (...args) => end(map(filter(slideList), args))
    }

    function makeAsync(path, query, sole) {
      const sync = makeSync(path, query, sole)
      const one = (...args) => $q.resolve(sync(...args))
      const all = (...args) => $q.all(sync(...args))
      return (...args) => sole ? one(...args) : all(...args)
    }
  }

  function Slide(media, index) {
    const sign = `gallerySlider::Slide[id=${$ctrl.ggsPost.id}][index=${index}]`
    const slide = attachGetters(StrictObj({
      sign,
      exist: true,
      ready: false,
      media,
      index,
      wrapH: DefineHandler(sign),
      loaded: false,
      isStory: media.external_type === INSTAGRAM_STORY,
      extEmbed: LazyResource('allow', 'debounce', 'disallow'),
      extContent: LazyResource('applySize').sync('calcWidth', 'calcHeight'),
      hasTextContent: isTextContent(media),
      hasImageContent: isImageContent(media),
      hasEmbedContent: isEmbedContent(media),
      hasBrokenContent: true
        && !isTextContent(media)
        && !isImageContent(media)
        && !isEmbedContent(media)
    }))

    if (!slide.hasEmbedContent) {
      slide.extEmbed.allow.replace(noop)
      slide.extEmbed.debounce.replace(noop)
      slide.extEmbed.disallow.replace(noop)
    }

    slide.wrapH('embedLoadHandler', () => {
      slide.loaded = true
      return $ctrl.callEmbedStateHandler()
    })

    slide.wrapH('embedUnloadHandler', () => {
      slide.loaded = false
      return $ctrl.callEmbedStateHandler()
    })

    slide.wrapH('contentReadyHandler', () => {
      slide.ready = true
      return qh.join(
        $ctrl.blinkBadge(),
        $ctrl.callEmbedActionMethod(),
        $ctrl.callContentReadyHandler())
    })

    slide.wrapH('contentChangeHandler', () => {
      return $ctrl.callContentChangeHandler()
    })

    slide.wrapH('embedAttachCallback', (extEmbedAllow, extEmbedDebounce, extEmbedDisallow) => {
      slide.extEmbed.allow.replace(extEmbedAllow)
      slide.extEmbed.debounce.replace(extEmbedDebounce)
      slide.extEmbed.disallow.replace(extEmbedDisallow)
    })

    slide.wrapH('contentAttachCallback', (extContentCalcWidth, extContentCalcHeight, extContentApplySize) => {
      slide.extContent.calcWidth.replace(extContentCalcWidth)
      slide.extContent.calcHeight.replace(extContentCalcHeight)
      slide.extContent.applySize.replace(extContentApplySize)
    })

    return slide
  }
}
